import { useState } from 'react';
import { ArrowLeft, AlertTriangle, CheckCircle2, XCircle, Loader2, History } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { useImportJobs, type ImportJobStatus, type ImportJobSummary } from '@/hooks/use-import-jobs';
import { useLocationStore } from '@/stores/location-store';

function StatusBadge({ status }: { status: ImportJobStatus }) {
  const styles: Record<ImportJobStatus, { bg: string; text: string; icon: React.ReactNode; label: string }> = {
    running: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
      label: 'Running',
    },
    succeeded: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      icon: <CheckCircle2 className="h-3 w-3" />,
      label: 'Succeeded',
    },
    failed: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      icon: <XCircle className="h-3 w-3" />,
      label: 'Failed',
    },
  };
  const s = styles[status];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${s.bg} ${s.text}`}>
      {s.icon}
      {s.label}
    </span>
  );
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(startIso: string | null, endIso: string | null): string {
  if (!startIso || !endIso) return '—';
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (ms < 0) return '—';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  return `${min}m ${remSec}s`;
}

export function ImportHistoryPage() {
  const [selected, setSelected] = useState<ImportJobSummary | null>(null);
  const { selectedLocationId } = useLocationStore();
  const { data, isLoading, refetch } = useImportJobs({
    limit: 50,
    locationId: selectedLocationId,
  });
  const jobs = data?.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader title="Import History">
        <div className="flex items-center gap-2">
          <a
            href="#/pos-management"
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Products
          </a>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Refresh
          </button>
        </div>
      </PageHeader>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Loading...
        </div>
      ) : jobs.length === 0 ? (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            icon={History}
            title="No bulk imports yet"
            description="Bulk product imports you run will be listed here."
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">When</th>
                <th className="px-4 py-3 text-left font-medium">Who</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Rows</th>
                <th className="px-4 py-3 text-right font-medium">Inserted</th>
                <th className="px-4 py-3 text-right font-medium">Updated</th>
                <th className="px-4 py-3 text-right font-medium">Not imported</th>
                <th className="px-4 py-3 text-right font-medium">Errors</th>
                <th className="px-4 py-3 text-right font-medium">Duration</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const r = job.result;
                return (
                  <tr
                    key={job.id}
                    onClick={() => setSelected(job)}
                    className="cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/30"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(job.createdAt)}</td>
                    <td className="px-4 py-3">{job.createdByName || '—'}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{job.totalRows.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-green-700">
                      {r ? r.inserted.toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-blue-700">
                      {r ? r.updated.toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-red-700">
                      {r ? (r.failed ?? 0).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {job.errors?.length ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle className="h-3 w-3" />
                          {job.errors.length}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                      {formatDuration(job.startedAt, job.finishedAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && <JobDetailModal job={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function JobDetailModal({ job, onClose }: { job: ImportJobSummary; onClose: () => void }) {
  const r = job.result;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl rounded-lg border border-border bg-card p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">Import Details</h3>
          <StatusBadge status={job.status} />
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Started</dt>
          <dd>{formatDateTime(job.createdAt)}</dd>
          <dt className="text-muted-foreground">Finished</dt>
          <dd>{formatDateTime(job.finishedAt)}</dd>
          <dt className="text-muted-foreground">Duration</dt>
          <dd>{formatDuration(job.startedAt, job.finishedAt)}</dd>
          <dt className="text-muted-foreground">Uploaded by</dt>
          <dd>{job.createdByName || '—'}</dd>
          <dt className="text-muted-foreground">Total rows</dt>
          <dd className="tabular-nums">{job.totalRows.toLocaleString()}</dd>
          <dt className="text-muted-foreground">Processed</dt>
          <dd className="tabular-nums">{job.processedRows.toLocaleString()}</dd>
        </dl>

        {r && (
          <div className="mt-6">
            <h4 className="mb-2 text-sm font-semibold">Result</h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md bg-green-50 p-3">
                <div className="text-xs text-green-700">Inserted</div>
                <div className="text-lg font-bold text-green-800 tabular-nums">{r.inserted.toLocaleString()}</div>
              </div>
              <div className="rounded-md bg-blue-50 p-3">
                <div className="text-xs text-blue-700">Updated</div>
                <div className="text-lg font-bold text-blue-800 tabular-nums">{r.updated.toLocaleString()}</div>
              </div>
              {/* Replaces the old Depts/Cats/Vendors "created" tiles — import
                  matches those by name now and creates none of them, so the
                  number worth showing is how many rows that cost. */}
              <div className="rounded-md bg-red-50 p-3">
                <div className="text-xs text-red-700">Not imported</div>
                <div className="text-lg font-bold text-red-800 tabular-nums">{(r.failed ?? 0).toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}

        {job.errors && job.errors.length > 0 && (
          <div className="mt-6">
            <h4 className="mb-2 flex items-center gap-1 text-sm font-semibold text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              {job.errors.length} warning{job.errors.length === 1 ? '' : 's'}
            </h4>
            <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-muted/30 p-3">
              <ul className="space-y-1 text-xs">
                {job.errors.map((err, i) => (
                  <li key={i} className="font-mono text-muted-foreground">
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
